// YNX — DEMO MODE banner + read-only protection
// ───────────────────────────────────────────────────────────────
// Shown when the user is viewing the public demo workspace.
// All write actions in the UI must call `guardDemoWrite(workspace)`
// which throws if the workspace is read-only.
// ───────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { Sparkles, X, ArrowRight, Eye, Lock } from 'lucide-react';
import { track } from '../lib/tracker';

export const isDemoWorkspace = (workspace) => Boolean(workspace?.is_demo);

/** Open a friendly upgrade modal when a demo user tries to write. */
export const guardDemoWrite = (workspace, onUpgrade) => {
  if (!isDemoWorkspace(workspace)) return true; // allowed
  if (typeof window !== 'undefined') {
    track('demo_blocked_action');
    window.dispatchEvent(new CustomEvent('ynx-demo-blocked'));
  }
  return false;
};

export const DemoBanner = ({ workspace, onLeave, onSubscribe }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const isDemo = isDemoWorkspace(workspace);

  React.useEffect(() => {
    if (!isDemo) return;
    const handler = () => setModalOpen(true);
    window.addEventListener('ynx-demo-blocked', handler);
    return () => window.removeEventListener('ynx-demo-blocked', handler);
  }, [isDemo]);

  if (!isDemo) return null;

  return (
    <>
      <div className='ynx-demo-banner' data-testid='demo-banner'>
        <div className='ynx-demo-banner-inner'>
          <span className='ynx-demo-banner-pill'>
            <Eye size={11} /> MODE DÉMONSTRATION
          </span>
          <span className='ynx-demo-banner-text'>
            Données fictives — exploration libre, modifications désactivées.
          </span>
          <button
            className='ynx-demo-banner-cta'
            onClick={() => { track('demo_subscribe_click', { source: 'banner' }); if (onSubscribe) onSubscribe(); }}
            data-testid='demo-banner-subscribe'
          >
            <Sparkles size={11} /> S'abonner pour débloquer <ArrowRight size={11} />
          </button>
          <button
            className='ynx-demo-banner-close'
            onClick={onLeave}
            title='Quitter la démo'
            data-testid='demo-banner-leave'
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className='ynx-demo-modal-wrap' onClick={() => setModalOpen(false)}>
          <div className='ynx-demo-modal' onClick={(e) => e.stopPropagation()} data-testid='demo-blocked-modal'>
            <button className='ynx-demo-modal-close' onClick={() => setModalOpen(false)}><X size={16} /></button>
            <div className='ynx-demo-modal-icon'><Lock size={28} /></div>
            <h3>Modification désactivée en démo</h3>
            <p>
              Vous explorez actuellement l'espace YNX en lecture seule.
              Pour créer vos propres factures, clients, RDV et tâches,
              démarrez votre essai gratuit en 60 secondes.
            </p>
            <div className='ynx-demo-modal-actions'>
              <button
                className='ynx-demo-modal-primary'
                onClick={() => { setModalOpen(false); if (onSubscribe) onSubscribe(); }}
                data-testid='demo-modal-start-trial'
              >
                <Sparkles size={14} /> Démarrer mon essai gratuit
              </button>
              <button className='ynx-demo-modal-secondary' onClick={() => setModalOpen(false)}>
                Continuer la visite
              </button>
            </div>
            <p className='ynx-demo-modal-foot'>
              ✓ Aucune carte bancaire · ✓ Setup en 60 secondes · ✓ Vos données ne sont pas perdues
            </p>
          </div>
        </div>
      )}
    </>
  );
};
